<template>
  <div>
    <v-subheader class="px-0">{{ $t("COMPANIES.ADDRESSES") }}</v-subheader>
    <p class="caption grey--text mb-3">{{ $t("COMPANIES.ADDRESSES_HINT") }}</p>

    <v-alert v-if="!companyId" type="info" dense text>
      {{ $t("COMPANIES.SAVE_COMPANY_FIRST") }}
    </v-alert>

    <template v-else>
      <v-simple-table v-if="addresses.length" dense>
        <thead>
          <tr>
            <th>{{ $t("COMPANIES.ADDRESS_LABEL") }}</th>
            <th>{{ $t("COMPANIES.ADDRESS") }}</th>
            <th>{{ $t("COMPANIES.ADDRESS_CITY") }}</th>
            <th>{{ $t("GENERAL.LABELS.COUNTRY") }}</th>
            <th></th>
            <th v-if="canEdit"></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="address in addresses" :key="address.id">
            <td>{{ address.label || "-" }}</td>
            <td class="text-pre-wrap">{{ address.address }}</td>
            <td>{{ [address.postal_code, address.city].filter(Boolean).join(" ") || "-" }}</td>
            <td>{{ address.country?.label || address.country?.name || "-" }}</td>
            <td>
              <v-chip v-if="address.is_default_delivery" x-small class="mr-1" color="primary" dark>
                {{ $t("COMPANIES.DEFAULT_DELIVERY") }}
              </v-chip>
              <v-chip v-if="address.is_default_loading" x-small color="teal" dark>
                {{ $t("COMPANIES.DEFAULT_LOADING") }}
              </v-chip>
            </td>
            <td v-if="canEdit" class="text-right">
              <v-btn icon small @click="openEdit(address)"><v-icon small>mdi-pencil</v-icon></v-btn>
              <v-btn icon small @click="remove(address)"><v-icon small>mdi-delete</v-icon></v-btn>
            </td>
          </tr>
        </tbody>
      </v-simple-table>
      <v-alert v-else type="info" dense text>{{ $t("COMPANIES.NO_ADDRESSES") }}</v-alert>

      <v-btn
        v-if="canEdit"
        class="mt-3"
        small
        color="primary"
        :loading="isSaving"
        @click="openCreate"
      >
        {{ $t("COMPANIES.ADD_ADDRESS") }}
      </v-btn>
    </template>

    <v-dialog v-model="dialog" max-width="640">
      <v-card>
        <v-card-title>{{ editingId ? $t("COMPANIES.EDIT") : $t("COMPANIES.ADD_ADDRESS") }}</v-card-title>
        <v-card-text>
          <v-text-field v-model="form.label" :label="$t('COMPANIES.ADDRESS_LABEL')" />
          <v-textarea
            v-no-autofill
            v-model="form.address"
            rows="2"
            auto-grow
            :label="$t('COMPANIES.ADDRESS') + '*'"
          />
          <v-row>
            <v-col cols="12" md="4">
              <v-text-field v-no-autofill v-model="form.city" :label="$t('COMPANIES.ADDRESS_CITY')" />
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field v-no-autofill v-model="form.postal_code" :label="$t('COMPANIES.ADDRESS_POSTAL_CODE')" />
            </v-col>
            <v-col cols="12" md="4">
              <v-autocomplete
                v-no-autofill
                v-model="form.country_id"
                :items="countries"
                item-text="label"
                item-value="id"
                :label="$t('GENERAL.LABELS.COUNTRY')"
                clearable
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" md="6">
              <v-text-field v-model="form.contact_name" :label="$t('COMPANIES.ADDRESS_CONTACT')" />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field v-model="form.phone" :label="$t('COMPANIES.ADDRESS_PHONE')" />
            </v-col>
          </v-row>
          <v-checkbox v-model="form.is_default_delivery" :label="$t('COMPANIES.DEFAULT_DELIVERY')" hide-details dense />
          <v-checkbox v-model="form.is_default_loading" :label="$t('COMPANIES.DEFAULT_LOADING')" hide-details dense />
          <v-textarea v-model="form.notes" rows="1" auto-grow :label="$t('COMPANIES.NOTES')" class="mt-2" />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="dialog = false">{{ $t("GENERAL.LABELS.CANCEL") }}</v-btn>
          <v-btn color="primary" :loading="isSaving" :disabled="!form.address" @click="save">
            {{ $t("GENERAL.LABELS.SAVE") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import {
  GET_COMPANY_ADDRESSES,
  CREATE_COMPANY_ADDRESS,
  UPDATE_COMPANY_ADDRESS,
  DELETE_COMPANY_ADDRESS,
} from "@/store/companies.module";
import { SHOW_SNACK } from "@/store/snack.module";

const emptyForm = () => ({
  label: "",
  address: "",
  city: "",
  postal_code: "",
  country_id: null,
  contact_name: "",
  phone: "",
  is_default_delivery: false,
  is_default_loading: false,
  notes: "",
});

export default {
  name: "CompanyAddressesPanel",
  props: {
    companyId: { type: [Number, String], default: null },
    countries: { type: Array, default: () => [] },
    canEdit: { type: Boolean, default: true },
  },
  data() {
    return {
      addresses: [],
      dialog: false,
      editingId: null,
      isSaving: false,
      form: emptyForm(),
    };
  },
  watch: {
    companyId: {
      immediate: true,
      handler() {
        this.load();
      },
    },
  },
  methods: {
    async load() {
      if (!this.companyId) {
        this.addresses = [];
        return;
      }
      this.addresses = await this.$store.dispatch(GET_COMPANY_ADDRESSES, this.companyId);
    },
    openCreate() {
      this.editingId = null;
      this.form = emptyForm();
      this.dialog = true;
    },
    openEdit(address) {
      this.editingId = address.id;
      this.form = {
        label: address.label || "",
        address: address.address || "",
        city: address.city || "",
        postal_code: address.postal_code || "",
        country_id: address.country_id || null,
        contact_name: address.contact_name || "",
        phone: address.phone || "",
        is_default_delivery: !!address.is_default_delivery,
        is_default_loading: !!address.is_default_loading,
        notes: address.notes || "",
      };
      this.dialog = true;
    },
    async save() {
      this.isSaving = true;
      try {
        const params = { ...this.form };
        if (this.editingId) {
          await this.$store.dispatch(UPDATE_COMPANY_ADDRESS, {
            companyId: this.companyId,
            id: this.editingId,
            params,
          });
        } else {
          await this.$store.dispatch(CREATE_COMPANY_ADDRESS, {
            companyId: this.companyId,
            params,
          });
        }
        this.dialog = false;
        await this.load();
        this.$emit("updated", this.addresses);
      } catch (errors) {
        this.$store.dispatch(SHOW_SNACK, {
          message: errors?.message || this.$t("GENERAL.UNEXPECTED_ERROR"),
          color: "error",
        });
      } finally {
        this.isSaving = false;
      }
    },
    async remove(address) {
      if (!confirm(this.$t("COMPANIES.CONFIRM_DELETE_ADDRESS"))) {
        return;
      }
      await this.$store.dispatch(DELETE_COMPANY_ADDRESS, {
        companyId: this.companyId,
        id: address.id,
      });
      await this.load();
      this.$emit("updated", this.addresses);
    },
  },
};
</script>
<style scoped>
.text-pre-wrap {
  white-space: pre-wrap;
}
</style>
